/**
 * `entities/taxonomy-dataset` mutations.
 *
 * Same conventions as the four sibling taxonomy slices: direct hey-api calls,
 * every mutation invalidates the list, detail-scoped ones also invalidate the
 * detail, and toasts live here so every write gives consistent feedback.
 */
import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { i18n } from '@/shared/i18n';
import { assetsHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { track } from '@/shared/analytics';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import { downloadFile, sanitizeDownloadFileName } from '@/shared/utils/downloadFile';
import type {
  ApplyRecordWritesResult,
  GenerateRecordCodesResult,
  RecordWrite,
  TaxonomyDataset,
  TaxonomyRecord,
} from '../model/types';
import { TAXONOMY_DATASET_QUERY_KEYS } from './query-keys';

const DATASETS_URL = '/api/v1/taxonomy-datasets';

/**
 * The server's stable identifier for a name that is already taken, on create,
 * rename and duplicate alike.
 *
 * Declared here rather than imported: the value is a WIRE CONTRACT, published
 * and deliberately stable (`DUPLICATE_DATASET_NAME` in the API's repository), and
 * the frontend does not import from the service. Keying the message on it rather
 * than on the 409 is what lets a second kind of conflict be added to these
 * routes later without this branch quietly claiming it.
 */

const DUPLICATE_DATASET_NAME = 'duplicate_dataset_name';

/**
 * The name-clash refusal, in the user's own language.
 *
 * The server's `detail` names the offending taxonomy, which the generic failure
 * key cannot - but it is an English sentence, so Spanish and Portuguese users
 * were being shown English at the one moment they need to understand what to do.
 * This keeps the information and loses the English.
 *
 * Returns undefined for any other failure, so a caller falls back to the
 * server's own sentence rather than this message claiming a conflict it does not
 * describe. Trimmed to match the sentence the server composes.
 */
const nameTakenMessage = (code: string | undefined, name: string): string | undefined =>
  code === DUPLICATE_DATASET_NAME
    ? i18n.t('knowledge:taxonomy.generationMutations.nameTakenNamed', { name: name.trim() })
    : undefined;

export interface CreateTaxonomyDatasetPayload {
  name: string;
  description?: string;
  taxonomyId: string;
  taxonomyVersionId: string;
  /** The campaign to file it under; `null` or absent ⇒ Ungrouped. */
  campaignId?: string | null;
}

export function useCreateTaxonomyDataset(options?: {
  onSuccess?: (created: TaxonomyDataset) => void;
  /**
   * A name the server refused as already taken.
   *
   * Separate from the generic failure because the modal renders it beside the
   * field that caused it. The client checks the same rule before enabling
   * Create, so reaching this means a concurrent create won the race — rare, and
   * the only path by which a user sees it.
   */
  onNameConflict?: (message: string) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateTaxonomyDatasetPayload) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: DATASETS_URL,
        // eslint-disable-next-line i18next/no-literal-string -- a wire header value, not copy
        headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
        body: payload,
      });
      if (!response.data) {
        throw new Error('Failed to create taxonomy');
      }
      return response.data as TaxonomyDataset;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_DATASET_QUERY_KEYS.list() });
      track('taxonomy_generation_created', { dataset_id: data.id, schema_id: data.taxonomyId });
      enqueueSnackbar(i18n.t('knowledge:taxonomy.generationMutations.created'), {
        variant: 'success',
      });
      options?.onSuccess?.(data);
    },
    onError: (error, payload) => {
      const { status, detail, code } = parseProblemDetail(
        error,
        i18n.t('knowledge:taxonomy.generationMutations.failedCreate'),
      );
      // A toast for a name clash would cover the form the user is still editing.
      if (status === 409) {
        options?.onNameConflict?.(nameTakenMessage(code, payload.name) ?? detail);
        return;
      }
      enqueueSnackbar(detail, { variant: 'error' });
    },
  });
}

export function useRenameTaxonomyDataset(id: string) {
  const queryClient = useQueryClient();
  const detailKey = TAXONOMY_DATASET_QUERY_KEYS.detail(id);

  /**
   * Bookkeeping for renames that overlap, which one round trip of typing is
   * enough to produce.
   *
   * `confirmed` is the last dataset the SERVER acknowledged, which is the only
   * safe thing to roll back to — a second rename's own snapshot is the first
   * one's optimistic name, so restoring it settles the cache on a name that was
   * never accepted. `issued` names the newest rename so an older failure cannot
   * overwrite a newer one still in flight, and `settled` is what lets a later,
   * separate rename know it is starting fresh and should re-read the cache.
   */
  const burst = useRef<{ issued: number; settled: number; confirmed?: TaxonomyDataset }>({
    issued: 0,
    settled: 0,
  });

  return useMutation({
    mutationFn: async (name: string) => {
      const response = await assetsHttp.patch({
        throwOnError: true,
        url: `${DATASETS_URL}/{id}`,
        path: { id },
        // eslint-disable-next-line i18next/no-literal-string -- a wire header value, not copy
        headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
        body: { name },
      });
      if (!response.data) {
        throw new Error('Failed to rename taxonomy');
      }
      return response.data as TaxonomyDataset;
    },
    /*
     * The optimistic write and its snapshot live on the mutation, not at the
     * call site.
     *
     * `MutationObserver.mutate` stores only the LATEST per-call options and
     * detaches the observer from the mutation still in flight, so a second
     * rename silently discards the first one's rollback. Mutation-level
     * callbacks survive that swap; per-call ones do not, and the snapshot has to
     * travel with whichever still runs.
     */
    onMutate: async (name: string) => {
      await queryClient.cancelQueries({ queryKey: detailKey, exact: true });
      const seq = (burst.current.issued += 1);
      // Nothing else in flight, so the cache holds server truth and this rename
      // opens a new burst.
      if (seq === burst.current.settled + 1) {
        burst.current.confirmed = queryClient.getQueryData<TaxonomyDataset>(detailKey);
      }
      queryClient.setQueryData<TaxonomyDataset>(detailKey, (current) =>
        current ? { ...current, name } : current,
      );
      return { seq };
    },
    onSuccess: (data) => {
      queryClient.setQueryData(detailKey, data);
      burst.current.confirmed = data;
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_DATASET_QUERY_KEYS.list() });
    },
    /*
     * The server's own sentence, not the generic failure. Names became unique
     * per schema, so the likeliest refusal here is a clash — and "Failed to
     * rename" tells someone nothing about which name to try instead. There is no
     * client-side pre-check available: this page never loads the dataset list.
     */
    onError: (error, name, context) => {
      // Only the newest rename may roll back: an older failure would erase an
      // optimistic name the user has since typed and which may yet be accepted.
      if (context?.seq === burst.current.issued) {
        queryClient.setQueryData(detailKey, burst.current.confirmed);
      }
      const { detail, code } = parseProblemDetail(
        error,
        i18n.t('knowledge:taxonomy.generationMutations.failedRename'),
      );
      enqueueSnackbar(nameTakenMessage(code, name) ?? detail, { variant: 'error' });
    },
    /*
     * `exact` so this does not cascade into `records(id)`, which nests under
     * `detail(id)`: a rename cannot change a single record, and the grid can be
     * large.
     */
    onSettled: (_data, _error, _name, context) => {
      burst.current.settled = Math.max(burst.current.settled, context?.seq ?? 0);
      if (burst.current.settled === burst.current.issued) {
        void queryClient.invalidateQueries({ queryKey: detailKey, exact: true });
      }
    },
  });
}

export function useDuplicateTaxonomyDataset(options?: {
  onSuccess?: (copy: TaxonomyDataset) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: `${DATASETS_URL}/{id}/duplicate`,
        path: { id },
        // eslint-disable-next-line i18next/no-literal-string -- a wire header value, not copy
        headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
        body: {},
      });
      if (!response.data) {
        throw new Error('Failed to duplicate taxonomy');
      }
      return response.data as TaxonomyDataset;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_DATASET_QUERY_KEYS.list() });
      track('taxonomy_generation_duplicated', { dataset_id: data.id });
      // The copy's records arrive unassembled, so the count is right but every
      // name is blank until someone edits them. Say so rather than letting an
      // empty Preview column look like a bug.
      enqueueSnackbar(i18n.t('knowledge:taxonomy.generationMutations.duplicated'), {
        variant: 'success',
      });
      options?.onSuccess?.(data);
    },
    /*
     * The server's explanation, and only ONE toast.
     *
     * Duplicate was the last write path here still doing neither. Without the
     * suppress header the global interceptor toasts as well, so a single failure
     * produced two - and this one said "Failed to duplicate", which does not
     * name the taken name the auto-naming raced and lost.
     */
    onError: (error) => {
      const { detail } = parseProblemDetail(
        error,
        i18n.t('knowledge:taxonomy.generationMutations.failedDuplicate'),
      );
      enqueueSnackbar(detail, { variant: 'error' });
    },
  });
}

/**
 * Moves a taxonomy to another campaign, or to Ungrouped with `null`.
 *
 * Its own mutation rather than a second argument to `useRenameTaxonomyDataset`,
 * even though both are the same PATCH. That one is optimistic and tracks a burst
 * of overlapping renames so only the newest failure may roll back; threading a
 * second, unrelated field through that state machine would mean a failed move
 * could revert a name the user had since changed. This one is plain: write, then
 * invalidate.
 */
export function useMoveTaxonomyDatasetToCampaign(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (campaignId: string | null) => {
      const response = await assetsHttp.patch({
        throwOnError: true,
        url: `${DATASETS_URL}/{id}`,
        path: { id },
        // eslint-disable-next-line i18next/no-literal-string -- a wire header value, not copy
        headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
        body: { campaignId },
      });
      return response.data as TaxonomyDataset;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_DATASET_QUERY_KEYS.detail(id) });
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_DATASET_QUERY_KEYS.list() });
      enqueueSnackbar(i18n.t('knowledge:taxonomy.campaign.moved'), { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar(i18n.t('knowledge:taxonomy.campaign.failedMove'), { variant: 'error' });
    },
  });
}

export function useDeleteTaxonomyDataset(options?: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await assetsHttp.delete({
        throwOnError: true,
        url: `${DATASETS_URL}/{id}`,
        path: { id },
        // eslint-disable-next-line i18next/no-literal-string -- a wire header value, not copy
        headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
      });
      return id;
    },
    onSuccess: (id) => {
      // Purge the dead detail BEFORE invalidating the index, or a mounted
      // detail query refetches and 404s on a successful delete.
      queryClient.removeQueries({ queryKey: TAXONOMY_DATASET_QUERY_KEYS.detail(id) });
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_DATASET_QUERY_KEYS.list() });
      track('taxonomy_generation_deleted', { dataset_id: id });
      enqueueSnackbar(i18n.t('knowledge:taxonomy.generationMutations.deleted'), {
        variant: 'success',
      });
      options?.onSuccess?.();
    },
    onError: () => {
      enqueueSnackbar(i18n.t('knowledge:taxonomy.generationMutations.failedDelete'), {
        variant: 'error',
      });
    },
  });
}

export function useAppendRecords(datasetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (count: number) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: `${DATASETS_URL}/{id}/records`,
        path: { id: datasetId },
        body: { count },
      });
      return (response.data as TaxonomyRecord[] | undefined) ?? [];
    },
    onSuccess: (appended) => {
      // Appended in place rather than refetched: the grid holds unsaved edits on
      // the rows already on screen, and a refetch would drop them.
      queryClient.setQueryData<TaxonomyRecord[]>(
        TAXONOMY_DATASET_QUERY_KEYS.records(datasetId),
        (current) => [...(current ?? []), ...appended],
      );
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_DATASET_QUERY_KEYS.list() });
    },
    onError: (error) => {
      // The server's own sentence when it has one — the record-cap refusal says
      // how full the taxonomy is, which a generic message would throw away.
      const problem = parseProblemDetail(
        error,
        i18n.t('knowledge:taxonomy.generationMutations.failedAddRecords'),
      );
      enqueueSnackbar(problem.detail, { variant: 'error' });
    },
  });
}

/**
 * Saves the grid's pending edits.
 *
 * The interceptor toast is suppressed and `throwOnError` kept on, so a 409
 * reaches the caller as a status it can act on — a duplicate generated name
 * needs the offending cell highlighted, not a generic red bar.
 */
export function useApplyRecordWrites(datasetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (writes: RecordWrite[]) => {
      const response = await assetsHttp.patch({
        throwOnError: true,
        url: `${DATASETS_URL}/{id}/records`,
        path: { id: datasetId },
        // eslint-disable-next-line i18next/no-literal-string -- a wire header value, not copy
        headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
        body: { writes },
      });
      if (!response.data) {
        throw new Error('Failed to save records');
      }
      return response.data as ApplyRecordWritesResult;
    },
    onSuccess: (result, writes) => {
      /**
       * Patch the cache from the response rather than invalidating: the global
       * staleTime is five minutes, so an invalidate alone can serve the pre-save
       * snapshot on a navigate-away-and-back and silently lose the edit.
       *
       * The patch has to carry the CELLS too, not only the revision. Advancing
       * the revision while leaving `cells` stale is worse than not patching at
       * all: the grid visibly reverts to the pre-save values, and the next edit
       * assembles its name from that stale document and sends it with a revision
       * the server accepts — so the optimistic-concurrency guard cannot catch it
       * and the stored `assembledName`, which is a permanent reporting join key
       * holding a unique-index slot, stops corresponding to the record's values.
       */
      const sent = new Map(writes.map((w) => [w.id, w]));
      queryClient.setQueryData<TaxonomyRecord[]>(
        TAXONOMY_DATASET_QUERY_KEYS.records(datasetId),
        (current) => {
          if (!current) {
            return current;
          }
          const byId = new Map(result.applied.map((a) => [a.id, a]));
          return current.map((record) => {
            const landed = byId.get(record.id);
            if (!landed) {
              return record;
            }
            const write = sent.get(record.id);
            return {
              ...record,
              // The server MERGES the patch into the stored cells, so the cache
              // has to merge it the same way or the two diverge.
              cells: write ? { ...record.cells, ...write.patch } : record.cells,
              outputs: write ? write.outputs : record.outputs,
              revision: landed.revision,
              assembledName: landed.assembledName,
            };
          });
        },
      );
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_DATASET_QUERY_KEYS.list() });
    },
    onError: (error) => {
      /**
       * The interceptor toast is suppressed so a 409 can be handled precisely,
       * which means NOTHING is shown unless this says something. A refused save
       * that looks identical to a successful one is worse than a generic red
       * bar: the user's only signal was the button becoming clickable again.
       *
       * The duplicate-name refusal carries the server's own sentence, which
       * explains that the clash may be in a taxonomy they are not looking at —
       * so it is surfaced verbatim rather than replaced.
       */
      const problem = parseProblemDetail(
        error,
        i18n.t('knowledge:taxonomy.generationMutations.failedSave'),
      );
      enqueueSnackbar(problem.detail, { variant: 'error', persist: problem.status === 409 });
    },
  });
}

export function useClearRecords(datasetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (writes: Pick<RecordWrite, 'id' | 'expectedRevision'>[]) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: `${DATASETS_URL}/{id}/records/clear`,
        path: { id: datasetId },
        body: { writes },
      });
      if (!response.data) {
        throw new Error('Failed to clear records');
      }
      return response.data as ApplyRecordWritesResult;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: TAXONOMY_DATASET_QUERY_KEYS.records(datasetId),
      });
    },
    onError: () => {
      enqueueSnackbar(i18n.t('knowledge:taxonomy.generationMutations.failedClear'), {
        variant: 'error',
      });
    },
  });
}

/**
 * Downloads the dataset as a workbook.
 *
 * Server-side generation, so the browser never loads exceljs — a megabyte of
 * bundle for an action most sessions never take. The response is a binary body,
 * so this asks for a blob rather than letting the client parse it as JSON.
 */
/**
 * @param fileName the name WITHOUT its extension. The browser names a `blob:`
 * download from `a.download` alone — a blob URL carries no `Content-Disposition`
 * — so the server's own filename never reaches the user and the caller owns this.
 */
export function useExportRecords(datasetId: string, fileName: string) {
  return useMutation({
    mutationFn: async () => {
      const response = await assetsHttp.get({
        throwOnError: true,
        url: `${DATASETS_URL}/{id}/records/export`,
        path: { id: datasetId },
        responseType: 'blob',
      });
      return response.data as Blob;
    },
    onSuccess: async (blob) => {
      await downloadFile({
        blob,
        // eslint-disable-next-line i18next/no-literal-string -- a filename fallback, not copy
        fileName: `${sanitizeDownloadFileName(fileName, 'taxonomy')}.xlsx`,
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      track('taxonomy_records_exported', { dataset_id: datasetId });
    },
    onError: () => {
      enqueueSnackbar(i18n.t('knowledge:taxonomy.generationMutations.failedExport'), {
        variant: 'error',
      });
    },
  });
}

/**
 * Mints tracking codes for the selected records.
 *
 * Does NOT invalidate the record cache, and that is deliberate: the endpoint
 * returns codes without storing them, so the server's copy of these records has
 * not changed. Refetching would wipe the codes out of the grid before the caller
 * has written them.
 *
 * The interceptor toast is suppressed so the caller can report the two outcomes
 * this call really has — minted and skipped — instead of a generic red bar over
 * a request that partly succeeded.
 */
export function useGenerateRecordCodes(datasetId: string) {
  return useMutation({
    mutationFn: async ({ ids, fieldId }: { ids: string[]; fieldId: string }) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: `${DATASETS_URL}/{id}/records/tracking-codes`,
        path: { id: datasetId },
        // eslint-disable-next-line i18next/no-literal-string -- a wire header value, not copy
        headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
        body: { ids, fieldId },
      });
      if (!response.data) {
        throw new Error('Failed to generate tracking codes');
      }
      return response.data as GenerateRecordCodesResult;
    },
    onSuccess: (result) => {
      track('taxonomy_record_codes_generated', {
        dataset_id: datasetId,
        minted_count: result.minted.length,
        spent_count: result.spent,
        skipped_count: result.skipped.length,
      });
    },
    onError: (error) => {
      /**
       * The server's own text, not a generic failure: the refusal a caller will
       * actually hit is the budget one, and it names how many codes the schema
       * has left. A generic bar would hide the only number that matters.
       */
      const problem = parseProblemDetail(
        error,
        i18n.t('knowledge:taxonomy.generationMutations.failedGenerateCodes'),
      );
      /*
       * A failure does NOT mean nothing was spent. The server allocates and
       * commits before it answers, so a response lost in transit leaves the code
       * issued — invisibly, out of a hard lifetime allowance.
       *
       * The reassurance is true on EVERY failure, including a budget refusal,
       * because it is a property of the mint and not of this attempt: it is
       * anchored on the record, so asking again returns whatever that record
       * already holds and allocates nothing.
       *
       * One snackbar carrying both, not an error plus an info beneath it. Two
       * stack, and the second reads as a separate event rather than as the first
       * one's "so what do I do": the user dismisses one and loses the half that
       * told them retrying is safe.
       */
      enqueueSnackbar(
        i18n.t('knowledge:taxonomy.generationMutations.codesFailedRetryIsFree', {
          detail: problem.detail,
        }),
        { variant: 'error' },
      );
    },
  });
}

export function useDeleteRecords(datasetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: `${DATASETS_URL}/{id}/records/bulk-delete`,
        path: { id: datasetId },
        body: { ids },
      });
      return (response.data as { deletedCount: number } | undefined) ?? { deletedCount: 0 };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: TAXONOMY_DATASET_QUERY_KEYS.records(datasetId),
      });
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_DATASET_QUERY_KEYS.list() });
    },
    onError: () => {
      enqueueSnackbar(i18n.t('knowledge:taxonomy.generationMutations.failedDeleteRecords'), {
        variant: 'error',
      });
    },
  });
}
